import { Box, Container, Grid, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import pdf from "../../../image/pdf.jpg";
import uploadImage from "../../../image/wmremove-transformed-Photorooms.jpg";
import {
  wholesalerAddRequest,
  wholesalerResetState,
} from "../../../redux/wholesaler/WholesalerAction";
import "../../../style/style.css";
import { StyledContainer, styles } from "../../components/formStyles";
import CustomButton from "../../constant/commonButton";
import ReusableFormFields from "../../constant/reusableFormFields";
import { mapBackendMessage } from "../../notification/BackendMessage";
import CommonDialog from "../../notification/Dialogbox";
import CommonSnackbar from "../../notification/Snackbar";

const inputFields = [
  {
    name: "reg_no",
    label: "reg_no",
    rules: { required: "reg_no  is required" },
  },
  {
    name: "first_name",
    label: "First Name",
    rules: { required: "First Name is required" },
  },
  {
    name: "last_name",
    label: "Last Name",
    rules: { required: "Last Name is required" },
  },
  {
    name: "email",
    label: "Email Address",
    rules: {
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email format",
      },
    },
  },
  {
    name: "contact",
    label: "Phone No",
    rules: {
      required: "Phone No is required",
      pattern: {
        value: /^\d{8,}$/,
        message: "Invalid Phone Number",
      },
    },
  },
  {
    name: "address",
    label: "Address",
    size: "large",
    rules: { required: "Address is required" },
  },
  {
    name: "working_time",
    label: "Shop Open and Close time",
    rules: { required: "Shop Open and Close time is required" },
  },
];

const AddWholesaler = () => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const dialogTriggerRef = useRef();
  const [imageFileName, setImageFileName] = useState("");
  const [proofFileName, setProofFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [proofSelected, setProofSelected] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);
  const fileInputRef = useRef(null);
  const proofInputRef = useRef(null);

  const addWholesalerState = useSelector((state) => state?.wholesaler?.addWholesaler || {});

  const MAX_FILE_SIZE = 200 * 1024;
  const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png"];
  const PROOF_ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "application/pdf",
  ];

  const readFile = (file, onLoadCallback) => {
    const reader = new FileReader();
    reader.onload = () => onLoadCallback(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        showSnackbar("Please select a file smaller than 200kB.", "error");
        return;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        showSnackbar(
          "Invalid file type. Only JPEG and PNG are allowed.",
          "error"
        );
        return;
      }
      readFile(file, (result) => {
        const base64String = result.split(",")[1];
        setSelectedFile(base64String);
        setImagePreview(URL.createObjectURL(file));
        setImageFileName(file.name);
        setValue("fileName", file.name);
      });
    }
  };

  const handleProofChange = (event) => {
    const proof = event.target.files[0];
    if (proof) {
      if (proof.size > MAX_FILE_SIZE) {
        showSnackbar("Please select a file smaller than 200kB.", "error");
        return;
      }
      if (!PROOF_ALLOWED_FILE_TYPES.includes(proof.type)) {
        showSnackbar(
          "Invalid file type. Only JPEG, PNG and PDF are allowed.",
          "error"
        );
        return;
      }
      readFile(proof, (result) => {
        const base64String = result.split(",")[1];
        setProofSelected(base64String);

        if (proof.type === "application/pdf") {
          setProofPreview(pdf);
        } else {
          setProofPreview(URL.createObjectURL(proof));
        }
        setProofFileName(proof.name);
        setValue("proofName", proof.name);
      });
    }
  };

  const handleImage = () => {
    if (selectedFile) {
      showSnackbar(
        `Product details saved with file: ${selectedFile.name}`,
        "success"
      );
    } else {
      showSnackbar("Please choose a file before saving.", "error");
    }
  };

  const proofImage = () => {
    if (proofSelected) {
      showSnackbar(
        `Product details saved with file: ${proofSelected.name}`,
        "success"
      );
    } else {
      showSnackbar("Please choose a file before saving.", "error");
    }
  };

  const onSubmit = (data) => {
    if (!selectedFile) {
      showSnackbar("Please choose a file before saving.", "error");

      return;
    }
    if (!proofSelected) {
      showSnackbar("Please choose a proof file before saving.", "error");
      return;
    }

    const formData = {
      ...data,
      bar_img_url: selectedFile,
      govt_img_url: proofSelected,
    };

    dispatch(wholesalerAddRequest(formData));
    reset();
  };

  const resetFileInputs = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setImageFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";

    setProofSelected(null);
    setProofPreview(null);
    setProofFileName("");
    if (proofInputRef.current) proofInputRef.current.value = "";
  };

  const handleAddWholesalerResponse = () => {
    if (addWholesalerState?.error) {
      const errorFields = addWholesalerState?.errorFields || {};

      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      } else {
        const customMessage = mapBackendMessage(
          addWholesalerState?.errormessage ||
            "An error occurred while adding the wholesaler."
        );
        setDialogMessage(customMessage);
        setIsSuccess(false);
        setDialogOpen(true);
        dispatch(wholesalerResetState());
      }
    } else if (addWholesalerState?.success) {
      setDialogMessage("Wholesaler Details Aggregated Successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      reset({
        first_name: "",
        last_name: "",
        name: "",
        address: "",
        email: "",
        contact: "",
        working_time: "",
      });
      resetFileInputs();
      dispatch(wholesalerResetState());
    }
  };

  const handleCancel = () => {
    reset({
      first_name: "",
      last_name: "",
      name: "",
      address: "",
      email: "",
      contact: "",
      working_time: "",
    });
    resetFileInputs();
  };

  useEffect(handleAddWholesalerResponse, [addWholesalerState]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    dialogTriggerRef.current?.focus();
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ marginLeft: "none" }}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box>
              <Typography variant="h5" gutterBottom sx={styles.title}>
              Register New Wholesaler
              </Typography>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Add Minimum 2 Images Wholesaler
              </Typography>
            </Box>
            <Box>
              <StyledContainer>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                    <img
                      src={imagePreview || uploadImage}
                      alt="Image"
                      width={80}
                      height={80}
                      style={styles.image}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Upload Actual Photos of Wholesaler, size less than 200KB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          ref={fileInputRef}
                          hidden
                          onChange={handleFileChange}
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(selectedFile)}
                        // disabled={!imageFileName}
                      >
                        {imageFileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={handleImage}
                        size="small"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={styles.note}>
                <span style={{ color: "red" }}>Note:</span> Only JPEG or PNG
                format is supported <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box sx={styles.fileBoxContainer}>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Upload Minimum 2 Govt ID Proof Of Wholesaler
              </Typography>
              <StyledContainer>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                    <img
                      src={proofPreview || uploadImage}
                      alt="Proof"
                      width={80}
                      height={80}
                      style={styles.image}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Upload Actual Photos of Proof, size less than 200KB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        text="Choose File"
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          ref={proofInputRef}
                          onChange={handleProofChange}
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(proofSelected)}
                        // disabled={!proofFileName}
                      >
                        {proofFileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={proofImage}
                        size="small"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={styles.note}>
                <span style={{ color: "red" }}>Note:</span> Only JPEG, PDF & PNG
                format is supported <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
          </Grid>
          {/* Input Fields */}
          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <ReusableFormFields
              inputFields={inputFields}
              control={control}
              errors={errors}
              required={true}
              register={register}
            />

            {/* Submit Button */}
            <Box sx={styles.submitGap}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={6}>
                  <CustomButton
                    text="Submit"
                    size="medium"
                    type="submit"
                    buttonStyle="StyledButton"
                    disabled={addWholesalerState?.inProgress}
                    loading={addWholesalerState?.inProgress}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomButton
                    text="Cancel"
                    variant="contained"
                    size="medium"
                    fullWidth
                    buttonStyle="style1"
                    onClick={handleCancel}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar */}
      <CommonSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      {/* Dialog */}
      <CommonDialog
        open={dialogOpen}
        isSuccess={isSuccess}
        onClose={handleDialogClose}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />
    </Container>
  );
};

export default AddWholesaler;
